import { dialog, modelName, sessionStorageKeyAuthorizationToken, unknownLabel } from '../../../common/const/commonConst';
import { responseType } from '../../../common/type/ResponseType';
import { CarInfo, DriverInfo, OrganizationInfo } from '../../../common/type/MasterInfoType';
import { HandlerOutputType } from '../../../common/type/HandlerType';
import { getSessionStorage } from '../../../common/utils/sessionStorageHandler';
import { nonNullable } from '../../../common/utils/nonNullable';
import { isOffsegResponseTypeLimitOverError, isResponseTypeError, isResponseTypeSessionNotExist } from '../../../common/utils/responseTypeChecker';
import { redirectToLogin } from '../../../common/utils/redirectToLogin';

import { RequestParameterType } from '../type/RequestParameter';
import { CarUseResultInfoType } from '../type/CarUseResultInfoType';
import { queryGetCarUseResult } from '../api/queryGetCarUseResult';

const unknownDriverName = 'ドライバー不明';

type MasterType = {
	offsegOrganizationInfoList: OrganizationInfo[];
	offsegCarInfoList: CarInfo[];
	offsegDriverInfoList: DriverInfo[];
	g500LiteCarInfoList: CarInfo[];
	g500LiteDriverInfoList: DriverInfo[];
};

/**
 * Offseg・G500Liteの車両利用実績を取得する
 */
export const getCarUseResultHandler = async (
	requestParams: RequestParameterType,
	master: MasterType,
): Promise<HandlerOutputType<CarUseResultInfoType[]>> => {
	// 認証トークンを取得する
	const authorizationToken = getSessionStorage(sessionStorageKeyAuthorizationToken);

	if (!authorizationToken) {
		return [dialog.systemError, undefined];
	}

	const getOffsegCarUseResultInput = {
		authorizationToken: authorizationToken,
		searchType: requestParams.searchType,
		organizationId: requestParams.offsegOrganizationId ? requestParams.offsegOrganizationId : undefined,
		carId: requestParams.offsegCarId ? requestParams.offsegCarId : undefined,
		driverId: requestParams.offsegDriverId ? requestParams.offsegDriverId : undefined,
		searchDate: requestParams.searchDate,
	};

	const getG500LiteCarUseResultInput = {
		authorizationToken: authorizationToken,
		organizationId: requestParams.g500LiteOrganizationId ? requestParams.g500LiteOrganizationId : undefined,
		carId: requestParams.g500LiteCarId ? requestParams.g500LiteCarId : undefined,
		searchDate: requestParams.searchDate,
	};

	try {
		// Offseg・G500Liteの車両利用実績一覧を取得する
		const [isApiError, carUseResult] = await queryGetCarUseResult(getOffsegCarUseResultInput, getG500LiteCarUseResultInput, authorizationToken);

		if (isApiError || !carUseResult) {
			return [dialog.apiError, undefined];
		}

		if (isOffsegResponseTypeLimitOverError(carUseResult.getOffsegCarUseResult)) {
			return [dialog.limitOverError, undefined];
		}

		if (isResponseTypeError(carUseResult)) {
			return [dialog.apiError, undefined];
		}

		if (isResponseTypeSessionNotExist(carUseResult)) {
			redirectToLogin();
		}

		// 取得結果の存在チェック
		if (
			carUseResult.getOffsegCarUseResult.responseType === responseType.notExistApiResponseData &&
			carUseResult.getG500LiteCarUseResult.responseType === responseType.notExistApiResponseData
		) {
			return [dialog.notExistSearchResult, undefined];
		}

		const carUseResultInfoList: CarUseResultInfoType[] = [];

		// 取得した車両現在位置一覧を画面用に成形
		carUseResult.getOffsegCarUseResult.carUseResults.filter(nonNullable).forEach((item) => {
			const organizationInfo = master.offsegOrganizationInfoList.find((organizationInfo) => organizationInfo.organizationId === item.organizationId);
			const carInfo = master.offsegCarInfoList.find((offsegCarInfo) => offsegCarInfo.carId === item.carId);
			const driverInfo = master.offsegDriverInfoList.find((offsegDriverInfo) => offsegDriverInfo.driverId === item.driverId);

			let driverName = unknownLabel;
			if (!item.driverId) {
				driverName = item.driverId === null ? unknownDriverName : unknownLabel;
			} else {
				driverName = driverInfo ? driverInfo.driverName : unknownLabel;
			}

			const carUseResultInfo = {
				numberPlate: item.numberPlate || unknownLabel,
				organizationName: !item.organizationId || !organizationInfo ? unknownLabel : organizationInfo.organizationName,
				carCode: !item.carId || !carInfo ? unknownLabel : carInfo.carCode,
				driverName: driverName,
				startDatetime: item.startDatetime || unknownLabel,
				endDatetime: item.endDatetime || unknownLabel,
				model: modelName.offseg,
			};

			carUseResultInfoList.push(carUseResultInfo);
		});

		carUseResult.getG500LiteCarUseResult.carUseResults.filter(nonNullable).forEach((item) => {
			const carInfo = master.g500LiteCarInfoList.find((g500LiteCarInfo) => g500LiteCarInfo.carId === item.carId);
			const driverInfo = master.g500LiteDriverInfoList.find((g500LiteDriverInfo) => g500LiteDriverInfo.driverId === item.driverId);

			const carUseResultInfo = {
				numberPlate: !item.carId || !carInfo ? unknownLabel : carInfo.numberPlate,
				organizationName: !item.carId || !carInfo || !carInfo.organizationName ? unknownLabel : carInfo.organizationName,
				carCode: item.carId || unknownLabel,
				driverName: !item.driverId || !driverInfo ? unknownLabel : driverInfo.driverName,
				startDatetime: item.startDatetime || unknownLabel,
				endDatetime: item.endDatetime || unknownLabel,
				model: modelName.g500Lite,
			};

			carUseResultInfoList.push(carUseResultInfo);
		});

		console.log(carUseResultInfoList);
		return [dialog.success, carUseResultInfoList];
	} catch (err: unknown) {
		return [dialog.systemError, undefined];
	}
};
